import {CookieStorage} from 'cookie-storage';
import {makeAutoObservable} from 'mobx';
import {getClientBrowserInfo} from '../helpers/browserInformation';
import LeadsApi from '../api/LeadsApi/LeadsApi';
import LoggingApi from '../api/LoggingApi/LoggingApi';
import {getAnonymousSessionId, maySendAnonymousEvent} from '../helpers/anonymousAnalytics';
import {ConsentCategory, hasConsent, isDoNotSellRequested} from '../helpers/consent';
import {stripSensitiveAnswers} from '../helpers/sensitiveAnswers';
import {currentSanitisedParams} from '../helpers/sanitiseParams';
import updateField from '../helpers/updateField';
import {type RootStore} from './Store';
import {serializable} from './sync/serialization';

class LogStore {
    private parentStore: RootStore;

    @serializable()
    browser = '';

    @serializable()
    os = '';

    @serializable()
    device = '';

    @serializable()
    screen_width = 0;

    @serializable()
    screen_height = 0;

    @serializable()
    orientation = '';

    @serializable()
    viewport = {
        width: 0,
        height: 0,
    };

    constructor(parentStore: RootStore) {
        this.parentStore = parentStore;
        makeAutoObservable(this);
    }

    // NOTE: This is here for backwards compatibility in mixpanel
    @serializable()
    get userId() {
        return this.parentStore.userId;
    }

    /**
     * The Mixpanel copy of a funnel event.
     *
     * Gated on Analytics consent per counsel: Mixpanel is a separate analytics copy, not
     * something needed to preserve the submission or determine qualification, so it is not
     * "necessary" merely by being in the pipeline. Operational writes are unaffected — the
     * application itself persists through saveSession, and lead events through LeadsApi,
     * both of which may flow immediately.
     *
     * Nothing is buffered for later replay: counsel asked for post-consent events only, with
     * no backfill of application answers, health or financial data, or qualification results.
     */
    async logAction(data: {action: string; data: any}): Promise<void> {
        if (!this.parentStore.userId) {
            throw new Error('A user session does not exist.');
        }

        if (!hasConsent(ConsentCategory.analytics)) {
            await this.logAnonymousAction(data.action);
            return;
        }

        const params = currentSanitisedParams();

        data['$browser'] = this.browser;
        data['$os'] = this.os;
        data['$device'] = this.device;
        data['$screen_height'] = this.screen_height;
        data['$screen_width'] = this.screen_width;
        data['$orientation'] = this.orientation;
        data['$viewport'] = this.viewport;
        data['params'] = params;
        data['eventOrigin'] = this.parentStore.uiStore.progressStore.currentSlideName;
        data['prosperFlow'] = this.parentStore.userData.useProsperFlow;

        // Include TU validation properties if available
        if (this.parentStore.userData.tuValidationStatus) {
            data['tu_validation_status'] = this.parentStore.userData.tuValidationStatus;
            data['tu_failure_reasons'] = this.parentStore.userData.tuFailureReasons || [];
        }

        await LoggingApi.log(this.userId, stripSensitiveAnswers(data), {
            useQueue: this.parentStore.userData.experiment.useQueue,
        });
    }

    /** The slide this visit entered on. Sessions resume, so it is not always the first one. */
    private anonymousEntrySlide = '';

    /**
     * The coarse counterpart to logAction, for visitors who have not answered the banner.
     *
     * Only the action name travels. The rich payload logAction assembles — the serialised
     * store, browser and viewport details, UTM params — is dropped entirely rather than
     * filtered, so nothing added to UserData later can leak through here.
     *
     * Conditional slides never reach this: SlidesWithProgressBar withholds their onView
     * entirely, so their answers cannot be inferred from the anonymous stream either.
     */
    private async logAnonymousAction(action: string): Promise<void> {
        if (!maySendAnonymousEvent()) return;

        const distinctId = getAnonymousSessionId();
        if (!distinctId) return;

        if (!this.anonymousEntrySlide) this.anonymousEntrySlide = action;

        try {
            await LoggingApi.logAnonymous({
                action,
                distinctId,
                entrySlide: this.anonymousEntrySlide,
                // Only flags that are actually split tell you anything: a pinned one is the same
                // for every visitor. Four are live today, which is roughly three bits — a cohort
                // of thousands, not a person. Were a lot of experiments live at once the
                // combination would start to work as a browser fingerprint joining this cohort
                // to the identified one, so pinning experiments when they conclude is what keeps
                // this safe, not the size of the object.
                experiment: this.parentStore.userData.experiment,
                utm: this.parentStore.userData.utm,
                params: currentSanitisedParams(),
                version: this.parentStore.userData.version,
            });
        } catch (error) {
            console.error('Failed to log anonymous funnel event', error);
        }
    }

    /**
     * Closes out the anonymous session when the visitor consents.
     *
     * Without it an anonymous session that ends because consent was granted is indistinguishable
     * from one that ends because the visitor left, so consents would be counted as abandonments.
     *
     * It carries no join key on purpose — no application id, no Mixpanel alias — so the two
     * cohorts reconcile in aggregate ("N anonymous sessions ended in consent, N identified
     * sessions began at consent") without linking any individual. Linking them for real is
     * mergeAnonymousSession below, which counsel approved separately on condition the privacy
     * policy discloses it; this event stays useful while that merge is switched off.
     */
    async logAnonymousSessionConsented(): Promise<void> {
        const distinctId = getAnonymousSessionId();
        if (!distinctId || !this.anonymousEntrySlide) return;

        try {
            await LoggingApi.logAnonymous({
                action: 'anonymous-session-consented',
                distinctId,
                entrySlide: this.anonymousEntrySlide,
                experiment: this.parentStore.userData.experiment,
                utm: this.parentStore.userData.utm,
                params: currentSanitisedParams(),
                version: this.parentStore.userData.version,
            });
        } catch (error) {
            console.error('Failed to close out anonymous session', error);
        }
    }

    /**
     * Joins this visit's pre-consent session to the identified profile, at the moment of consent.
     *
     * Nothing is re-sent: Mixpanel keeps the events already recorded under each id, with their
     * original timestamps, and re-attributes them to one profile. Only this visit is joined —
     * the anonymous id lives in sessionStorage, so earlier visits keep their own ids and stay
     * unlinked, which is the point.
     *
     * The backend decides whether to act; the merge is behind a secret there and is irreversible.
     */
    async mergeAnonymousSession(): Promise<void> {
        const anonymousId = getAnonymousSessionId();
        // No anonymous events were sent this visit, so there is no session to join.
        if (!anonymousId || !this.anonymousEntrySlide) return;
        if (!this.userId || anonymousId === this.userId) return;

        try {
            await LoggingApi.mergeIdentity({anonymousId, distinctId: this.userId});
        } catch (error) {
            console.error('Failed to merge anonymous session into the identified profile', error);
        }
    }

    /**
     * The identified funnel's entry point, replacing the old `basic-1st` anchor.
     *
     * Anything before consent is invisible to the identified stream — the first slide's onView
     * fires about a second after load, long before anyone can click Accept — so a funnel
     * anchored at the top of the walkthrough no longer has a denominator. This event is that
     * denominator, and its slide property shows how much of the funnel preceded it.
     */
    async logConsentGranted(): Promise<void> {
        await this.logAction({
            action: 'consent-granted',
            data: {consentedAtSlide: this.parentStore.uiStore.progressStore.currentSlideName || 'unknown'},
        });
    }

    /**
     * Lead events. Deliberately NOT gated on consent: this is the path that preserves the
     * consumer's submission and carries their do-not-sell choice, both of which counsel
     * confirmed may flow immediately. Gating it would drop the opt-out signal for exactly
     * the people who exercised it.
     */
    async logLead(event: string): Promise<void> {
        if (!this.parentStore.userId) {
            throw new Error('A user session does not exist.');
        }

        const urlParams = new URLSearchParams(window.location.search);
        const cookieStorage = new CookieStorage();
        const mediaSourceCookie = cookieStorage.getItem('mediaSource');
        const utmSource = urlParams.get('utm_source');
        const source = utmSource ? utmSource : mediaSourceCookie;
        const origin = urlParams.get('utm_origin');
        const partner_user_id = urlParams.get('partner_user_id');

        try {
            await LeadsApi.log(
                this.userId,
                source || 'direct_traffic',
                origin,
                event,
                partner_user_id || 'direct_traffic',
                isDoNotSellRequested(),
            );
        } catch (error) {
            throw new Error(`Failed to log lead event: ${event}`, {cause: error});
        }
    }

    updateField = updateField.bind(this);

    updateBrowserInfo(): void {
        const info = getClientBrowserInfo();

        this.os = info.os;
        this.browser = info.browser;
        this.device = info.device;
        this.screen_height = info.screenHeight;
        this.screen_width = info.screenWidth;
        this.orientation = info.orientation;
        this.viewport = info.viewport;
    }
}

export default LogStore;
